import dayjs from "dayjs";
import { extractDatePart } from "@/helpers/dateHelper";

type SearchFilters = {
  location: {
    lat: number | null;
    lng: number | null;
    address: string | null;
    placeId: string | null;
  };
  online: boolean;
  inPerson: boolean;
  dateFrom: Date;
  therapy: string;
  therapyType: string;
  radiusKm: number | null;
};

export function resolveSearchRadiusKm(
  value: unknown,
  radiusOptions: number[],
): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  if (!Array.isArray(radiusOptions) || !radiusOptions.length) {
    return parsed;
  }
  for (const option of radiusOptions) {
    if (parsed <= option) {
      return option;
    }
  }
  return radiusOptions[radiusOptions.length - 1];
}

export function parseSearchDate(rawValue: unknown, urlFormat?: string): Date {
  if (!rawValue) {
    return new Date();
  }
  if (rawValue instanceof Date) {
    const copy = new Date(rawValue.getTime());
    return Number.isNaN(copy.getTime()) ? new Date() : copy;
  }
  const value = String(rawValue);
  if (urlFormat) {
    const parsed = dayjs(value, urlFormat, true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
  }
  const datePart = extractDatePart(value);
  if (datePart) {
    const parsed = new Date(`${datePart}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  const fallback = new Date(value);
  if (!Number.isNaN(fallback.getTime())) {
    return fallback;
  }
  return new Date();
}

export function buildSearchFiltersFromQuery(
  query: Record<string, unknown>,
  radiusOptions: number[],
  urlFormat?: string,
): SearchFilters {
  const latParam =
    typeof query.lat === "string" ? Number.parseFloat(query.lat) : NaN;
  const lngParam =
    typeof query.lng === "string" ? Number.parseFloat(query.lng) : NaN;
  const radiusParam =
    typeof query.radius === "string" ? Number.parseFloat(query.radius) : NaN;

  const effectiveLat = Number.isFinite(latParam) ? latParam : null;
  const effectiveLng = Number.isFinite(lngParam) ? lngParam : null;
  const hasCoordinates =
    typeof effectiveLat === "number" && typeof effectiveLng === "number";
  const effectiveRadius = hasCoordinates
    ? resolveSearchRadiusKm(radiusParam, radiusOptions)
    : null;

  const onlineParam =
    typeof query.online === "string" ? query.online : undefined;
  const inPersonParam =
    typeof query.inPerson === "string" ? query.inPerson : undefined;
  // Match the UI default (both checked when nothing in URL). When a param is
  // present we honor "1" = on, anything else = off.
  const inPerson = inPersonParam === undefined ? true : inPersonParam === "1";
  const online = onlineParam === undefined ? true : onlineParam === "1";
  const dateFrom = parseSearchDate(query.dateFrom, urlFormat);

  return {
    location: {
      lat: typeof effectiveLat === "number" ? effectiveLat : null,
      lng: typeof effectiveLng === "number" ? effectiveLng : null,
      address:
        typeof query.address === "string" && query.address.length > 0
          ? query.address
          : null,
      placeId: null,
    },
    online,
    inPerson,
    dateFrom,
    therapy: typeof query.therapy === "string" ? query.therapy : "",
    therapyType: typeof query.therapyType === "string" ? query.therapyType : "",
    radiusKm: online
      ? null
      : typeof effectiveRadius === "number"
      ? effectiveRadius
      : null,
  };
}
